import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildGhlContactUrl } from "./ghlContactUrl";

interface BulkOpenInGHLProps {
  ids: string[];
  contactIdMap: Map<string, string | null>;
  locationId: string | null;
  getName: (convId: string) => string;
}

interface BlockedEntry {
  convId: string;
  url: string;
}

export const BulkOpenInGHL: React.FC<BulkOpenInGHLProps> = ({
  ids,
  contactIdMap,
  locationId,
  getName,
}) => {
  const [showFallback, setShowFallback] = useState(false);
  const [blockedEntries, setBlockedEntries] = useState<BlockedEntry[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const selectedAtClickRef = useRef<string[]>([]);

  const disabled = ids.length === 0 || locationId === null;
  const buttonTitle = locationId === null ? "Org has no GHL location configured" : undefined;

  const runOpenLoop = (idsToOpen: string[]): BlockedEntry[] => {
    if (!locationId) return [];
    const entries: BlockedEntry[] = [];
    for (const id of idsToOpen) {
      const contactId = contactIdMap.get(id);
      if (!contactId) continue;
      entries.push({ convId: id, url: buildGhlContactUrl(locationId, contactId) });
    }
    const blocked: BlockedEntry[] = [];
    for (const e of entries) {
      // NOTE: do NOT pass "noopener" in features — per spec window.open returns
      // null on success when noopener is set, which would make every successful
      // open look like a popup-block. Sever opener manually right after open
      // for the same security guarantee.
      const w = window.open(e.url, "_blank");
      if (w) {
        try {
          w.opener = null;
        } catch {
          /* cross-origin race: opener already inaccessible, which is the goal */
        }
      }
      if (!w || w.closed) blocked.push(e);
    }
    return blocked;
  };

  const handleClick = () => {
    if (disabled) return;
    const set = [...ids];
    selectedAtClickRef.current = set;
    const totalRequested = set.length;
    const blocked = runOpenLoop(set);
    const opened = totalRequested - blocked.length;
    const skipped = set.filter((id) => {
      const cid = contactIdMap.get(id);
      return !cid;
    }).length;
    setSkippedCount(skipped);
    if (blocked.length > 0) {
      setBlockedEntries(blocked);
      setShowFallback(true);
    } else if (opened === 0 && skipped === 0) {
      // Defensive: locationId went null between mount and click — surface as fallback w/ empty list
      setBlockedEntries([]);
      setShowFallback(true);
    }
  };

  const handleRetry = () => {
    const blocked = runOpenLoop(selectedAtClickRef.current);
    if (blocked.length === 0) {
      setShowFallback(false);
      setBlockedEntries([]);
    } else {
      setBlockedEntries(blocked);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={buttonTitle}
        data-testid="bulk-open-in-ghl-button"
        className="liquid-glass-light px-3 py-1 token-11 text-glass hover:text-glass disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Open in GHL ({ids.length})
      </button>
      {skippedCount > 0 && (
        <span className="token-10 text-glass-muted ml-2">
          {skippedCount} skipped (no GHL contact)
        </span>
      )}
      {showFallback &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
            data-testid="bulk-open-fallback-modal"
          >
            <div className="liquid-glass max-w-md w-full mx-4 p-5">
              <h3 className="token-16 font-semibold text-glass mb-2">
                Tabs were blocked by your browser
              </h3>
              <p className="token-12 text-glass-secondary mb-1">
                Brave/Chrome blocked {blockedEntries.length} pop-ups. To open them all at once,
                click the lock icon in the address bar &rarr; Site Settings &rarr; Pop-ups &rarr;
                Allow.
              </p>
              <p className="token-12 text-glass-secondary mb-3">
                Or click each link below to open one at a time:
              </p>
              <div className="max-h-60 overflow-y-auto mb-4 border-t border-b border-black/[0.08] py-2">
                {blockedEntries.map(({ convId, url }) => (
                  <a
                    key={convId}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="token-13m text-ds-accent hover:underline block py-1"
                    data-testid="bulk-open-fallback-link"
                  >
                    {getName(convId)}
                  </a>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowFallback(false)}
                  className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleRetry}
                  data-testid="bulk-open-fallback-retry"
                  className="liquid-glass-light px-3 py-1 token-11 text-glass hover:text-glass"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
